import React, { useMemo } from "react";
import { makeStyles } from "tss-react/mui";
import PropTypes from "prop-types";

import Box from "@mui/material/Box";

import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import Radio from "@mui/material/Radio";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ObjectSelectionArea from "./ObjectSelectionArea";
import { DATA_SOURCE } from "../../../../../util/Data/DataModeling";
import Alert from "@mui/material/Alert";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import SmallToolbar from "../../../../Common/SmallToolbar";

const useStyles = makeStyles()((theme) => ({
  PanelTitle: {
    color: "#ed6c02",
  },
  CheckBoxContainer: {
    width: "100%",
  },
}));

const FirstStepComponent = ({ applyAll, handleApplyAllChange, handleCheckboxCheck, modifiedObjects }) => {
  const { classes } = useStyles();
  const { sources = [], entities = [] } = useMemo(() => modifiedObjects, [modifiedObjects]);
  const deletedObjectsExist = useMemo(() => {
    return sources.some((source) => source.IsDeleted);
  }, [sources]);

  return (
    <>
      <Grid container sx={{ p: 0, m: 0 }} columnSpacing={0}>
        <Grid
          sx={{ display: "flex", alignItems: "center" }}
          size={{
            xs: 12,
            sm: 6,
            md: 3
          }}>
          <FormControlLabel
            value="applyAll"
            control={<Radio checked={applyAll} />}
            onChange={handleApplyAllChange}
            label={"Apply all objects"}
            labelPlacement="end"
          />
          <Tooltip
            placement="top"
            title={<Box sx={{ fontSize: "0.875rem" }}>This will apply all available objects in data modeling.</Box>}
            arrow
          >
            <InfoOutlinedIcon color="info" />
          </Tooltip>
        </Grid>
        <Grid
          sx={{ display: "flex", alignItems: "center" }}
          size={{
            xs: 12,
            sm: 6,
            md: 3
          }}>
          <FormControlLabel
            value="selectiveApply"
            control={<Radio checked={!applyAll} />}
            onChange={handleApplyAllChange}
            label="Apply selected objects"
            labelPlacement="end"
          />
          <Tooltip
            placement="top"
            title={
              <Box sx={{ fontSize: "0.875rem" }}>
                Only objects that were modified after the last apply to the warehouse are shown.
              </Box>
            }
            arrow
          >
            <InfoOutlinedIcon color="info" />
          </Tooltip>
        </Grid>
      </Grid>
      {sources && sources.length > 0 && (
        <Box className={classes.CheckBoxContainer}>
          <SmallToolbar className={classes.PanelTitle}>{DATA_SOURCE.plural}</SmallToolbar>
          <Box sx={{ width: "100%" }}>
            <ObjectSelectionArea
              objects={sources}
              applyAll={applyAll}
              handleCheckboxCheck={handleCheckboxCheck}
              objectType="sources"
            />
          </Box>
        </Box>
      )}
      {deletedObjectsExist && (
        <Alert
          icon={<WarningAmberRoundedIcon color="error" fontSize="large" />}
          sx={{
            mt: 1,
            mb: 2,
            padding: "0",
            paddingRight: "5px",
            paddingLeft: "5px",
            backgroundColor: "#ffe6e6",
            display: "flex",
            alignItems: "center",
            textAlign: "center",
          }}
          variant="outlined"
          severity="error"
        >
          Note: The dataset/entity with the “Deleted” tag will be <span style={{ color: "red" }}>permanently </span>
          deleted from the Snowflake data warehouse after Apply to Warehouse is completed.
        </Alert>
      )}
      {entities && entities.length > 0 && (
        <Box className={classes.CheckBoxContainer}>
          <Typography className={classes.PanelTitle}>Entities</Typography>
          <ObjectSelectionArea
            objects={entities}
            applyAll={applyAll}
            handleCheckboxCheck={handleCheckboxCheck}
            objectType="entities"
          />
        </Box>
      )}
    </>
  );
};

FirstStepComponent.propTypes = {
  applyAll: PropTypes.bool.isRequired,
  handleApplyAllChange: PropTypes.func.isRequired,
  handleCheckboxCheck: PropTypes.func.isRequired,
  modifiedObjects: PropTypes.object.isRequired,
};

export default React.memo(FirstStepComponent);
